import React, { useMemo } from 'react';
import ApexGlyph from './ApexGlyph';

/**
 * ApexHeroLogo
 * ------------
 * The cinematic primary hero placement of the APEX Verity brand mark.
 *
 * Composition (centered, vertical):
 *
 *           [  bell + V mark  ]                          ← ApexGlyph (large)
 *
 *              APEX VERITY                               ← wordmark
 *
 *      GOVERNANCE PROOF INFRASTRUCTURE                   ← tagline
 *
 * Motion (per brand brief):
 *   • A single dim "scanner" dot traverses the bell curve once every 14s.
 *   • The apex core dot performs a very slow shimmer pulse (14s cycle).
 *   • Nothing else moves. No floating particles. No neon glow.
 *   • `prefers-reduced-motion` disables all motion.
 *
 * Spacing:
 *   Caller is responsible for ≥ 1.5x logo-height of clear space.
 *
 * Props
 *   width — rendered max width in px (height auto from 2.5:1 aspect)
 *   showWordmark — toggle the typographic lockup (default true)
 *   showTagline  — toggle the small tracked-out tagline (default true)
 */
export default function ApexHeroLogo({
  width = 480,
  variant = 'default',
  showWordmark = true,
  showTagline = true,
}) {
  // Match the glyph's internal viewBox for path math.
  const glyphH = width / 2.5;

  // Derive the scanner-dot animation path from the same bell curve drawn in
  // ApexGlyph. Using the same Bezier coords (Fix 3 tighter crest) keeps the
  // dot visually anchored to the stroke.
  const motionPath = useMemo(
    () => 'M 4 90 L 90 90 C 125 90, 138 10, 150 10 C 162 10, 175 90, 210 90 L 296 90',
    []
  );

  return (
    <div
      data-testid="apex-hero-logo"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
        userSelect: 'none',
      }}
    >
      {/* The mark itself + the scanner overlay sit in a relatively positioned
          container so the motion SVG can layer perfectly on top at the same scale. */}
      <div style={{ position: 'relative', width, height: glyphH, lineHeight: 0 }}>
        <ApexGlyph size={glyphH} variant={variant} />

        {/* Scanner overlay — invisible path used for animateMotion only. */}
        <svg
          viewBox="0 0 300 120"
          width={width}
          height={glyphH}
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            overflow: 'visible',
          }}
          aria-hidden
        >
          <path id="apex-hero-motion-path" d={motionPath} fill="none" stroke="none" />
          {/* Fix 8 — scanner dot: smaller (r=1.2) and dimmer.
              The logo should feel alive without ever feeling animated. */}
          <circle r="1.2" fill="#E2E8F0" fillOpacity="0.55"
            style={{ filter: 'drop-shadow(0 0 3px rgba(226,232,240,0.3))' }}>
            <animateMotion dur="14s" repeatCount="indefinite" rotate="auto" calcMode="linear">
              <mpath href="#apex-hero-motion-path" />
            </animateMotion>
            <animate
              attributeName="opacity"
              values="0;0;0.85;0.85;0"
              keyTimes="0;0.05;0.2;0.8;1"
              dur="14s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      </div>

      {showWordmark && (
        <div
          style={{
            marginTop: Math.round(glyphH * 0.22),
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: `clamp(28px, ${(width / 480) * 44}px, 56px)`,
            letterSpacing: '0.38em',
            color: '#F1F5F9',
            textTransform: 'uppercase',
            textIndent: '0.38em', // optical compensation for trailing tracking
          }}
          data-testid="apex-hero-wordmark"
        >
          APEX&nbsp;Verity
        </div>
      )}

      {showTagline && (
        <div
          style={{
            marginTop: 18,
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: `clamp(10px, ${(width / 480) * 12}px, 13px)`,
            letterSpacing: '0.46em',
            color: '#475569',
            textTransform: 'uppercase',
            textIndent: '0.46em',
          }}
          data-testid="apex-hero-tagline"
        >
          Governance&nbsp;Proof&nbsp;Infrastructure
        </div>
      )}

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          [data-testid="apex-hero-logo"] animateMotion,
          [data-testid="apex-hero-logo"] animate {
            animation: none !important;
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
